import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { VoiceModuleHandler } from './voice-handler.interface';
import { VoiceIntent, VoiceExecutionResult, SessionContextEntity } from '../models';
import { VoiceContextService, ProjectMemberInfo } from '../services/voice-context.service';
import { VoiceSessionService } from '../services/voice-session.service';
import { MemberResolverService } from '../services/member-resolver.service';
import { ProjectService } from '../../../modules/project-management/services/project.service';
import { TaskGroupService } from '../../../modules/project-management/services/task-group.service';
import { ProjectReleaseVersionsService } from '../../../modules/project-management/services/project-release-versions.service';
import { TaskGroup } from '../../../modules/project-management/interfaces/task-group.interface';
import { ProjectReleaseVersion } from '../../../modules/project-management/interfaces/project-release-version.interface';

/** Forme brute d'une ligne `project_members` (jointure `profile:profiles(*)`), cf. ProjectVoiceHandler */
interface ProjectMemberRow {
  profile_id?: string;
  profile?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

/**
 * Handler de `session.setContext` (MOKA-207 Phase 4, Lot 3) : pose le
 * contexte conversationnel court terme (personne/sprint/release/échéance)
 * consommé comme défaut NON ÉCRASANT par les créations guidées (cf.
 * `VoiceGuidedFlowService`, garde-fou récap-confirmation). Le contrat
 * `session.setContext` est un no-op côté Edge Function — c'est CE handler,
 * dans le même registre que les autres `VoiceModuleHandler`
 * (`ServiceRouterService`), qui résout et persiste réellement le contexte.
 *
 * Résolution : `person`/`group`/`release` ne sont résolus en entité fine
 * (id + nom canonique) QUE si un projet est en contexte (session ou page) —
 * même source de vérité et même ordre de préséance que
 * `VoiceGuidedFlowService.#beginWithProjectStep` : `entities.lastProject`
 * (conversation) prime sur `VoiceContextService.currentProjectId()` (page).
 * Sans projet en contexte, ou si aucune correspondance n'est trouvée, le nom
 * brut prononcé/tapé est conservé tel quel (mieux qu'un échec sec : un
 * projet peut être précisé juste après, et l'utilisateur reste libre de
 * retirer ce contexte via les chips s'il est erroné). `dueDate` est transmis
 * tel quel (déjà résolu en `YYYY-MM-DD` côté GPT) — aucune validation de
 * format ici, cohérent avec les autres handlers qui font confiance à
 * `IntentValidatorService`.
 */
@Injectable({ providedIn: 'root' })
export class SessionVoiceHandler implements VoiceModuleHandler {
  private voiceContext = inject(VoiceContextService);
  private voiceSession = inject(VoiceSessionService);
  private memberResolver = inject(MemberResolverService);
  private projectService = inject(ProjectService);
  private taskGroupService = inject(TaskGroupService);
  private releaseService = inject(ProjectReleaseVersionsService);

  readonly supportedEndpoints = ['session.setContext'];

  async execute(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    if (intent.endpoint !== 'session.setContext') {
      return { success: false, message: `Action session non supportée: ${intent.endpoint}` };
    }

    const personRaw = this.#asString(intent.params['person']);
    const groupRaw = this.#asString(intent.params['group']);
    const releaseRaw = this.#asString(intent.params['release']);
    const dueDate = this.#asString(intent.params['dueDate']);

    const projectId = this.#currentProjectId();

    const [person, group, release] = await Promise.all([
      this.#resolvePerson(personRaw, projectId),
      this.#resolveGroup(groupRaw, projectId),
      this.#resolveRelease(releaseRaw, projectId)
    ]);

    const posed: string[] = [];

    if (person) {
      this.voiceSession.setContextEntity('person', person);
      posed.push(`personne : ${person.name}`);
    }
    if (group) {
      this.voiceSession.setContextEntity('group', group);
      posed.push(`sprint : ${group.name}`);
    }
    if (release) {
      this.voiceSession.setContextEntity('release', release);
      posed.push(`release : ${release.name}`);
    }
    if (dueDate) {
      this.voiceSession.setDueDateContext(dueDate);
      posed.push(`échéance : ${dueDate}`);
    }

    if (posed.length === 0) {
      return { success: true, message: 'Aucun contexte à poser.' };
    }

    return { success: true, message: `Contexte posé : ${posed.join(', ')}.` };
  }

  /** Même préséance que `VoiceGuidedFlowService.#beginWithProjectStep` : conversation avant page. */
  #currentProjectId(): string | null {
    return this.voiceSession.entities().lastProject?.id ?? this.voiceContext.currentProjectId();
  }

  async #resolvePerson(name: string | null, projectId: string | null): Promise<SessionContextEntity | null> {
    if (!name) return null;
    if (!projectId) return { name };

    try {
      const members = await firstValueFrom(this.projectService.getProjectMembers(projectId));
      const mapped: ProjectMemberInfo[] = (members as ProjectMemberRow[])
        .filter(m => !!m.profile)
        .map(m => ({
          profileId: m.profile_id || m.profile?.id || '',
          firstName: m.profile?.first_name || '',
          lastName: m.profile?.last_name || '',
          email: m.profile?.email || ''
        }));

      const match = this.memberResolver.resolve(name, mapped);
      if (match.confidence !== 'none' && match.memberId) {
        return { id: match.memberId, name: match.memberName };
      }
    } catch {
      // Résolution échouée : nom brut conservé plutôt qu'un échec sec.
    }

    return { name };
  }

  async #resolveGroup(name: string | null, projectId: string | null): Promise<SessionContextEntity | null> {
    if (!name) return null;
    if (!projectId) return { name };

    try {
      const groups = await firstValueFrom(this.taskGroupService.getByProject(projectId));
      const found = this.#matchByName(groups, (g: TaskGroup) => g.label, name);
      if (found) return { id: found.id, name: found.label };
    } catch {
      // idem : nom brut conservé si la résolution échoue.
    }

    return { name };
  }

  async #resolveRelease(name: string | null, projectId: string | null): Promise<SessionContextEntity | null> {
    if (!name) return null;
    if (!projectId) return { name };

    try {
      const releases = await firstValueFrom(this.releaseService.getByProject(projectId));
      const found = this.#matchByName(releases, (r: ProjectReleaseVersion) => r.name, name);
      if (found) return { id: found.id, name: found.name };
    } catch {
      // idem.
    }

    return { name };
  }

  /** Correspondance exacte (insensible à la casse) en priorité, sinon inclusion partielle — même esprit que `ProjectVoiceHandler.resolveProjectId`. */
  #matchByName<T>(items: T[], getName: (item: T) => string, needle: string): T | null {
    const normalized = needle.toLowerCase();
    return (
      items.find(item => getName(item).toLowerCase() === normalized) ??
      items.find(item => getName(item).toLowerCase().includes(normalized)) ??
      null
    );
  }

  #asString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }
}
